/* Shopify's section-rendered recommendations; no additional recommendation provider. */
(() => {
  if (customElements.get('fh-product-recommendations')) return;
  const MAX_BYTES = 512 * 1024, DEADLINE_MS = 8000;
  const withAbort = (promise, signal) => new Promise((resolve, reject) => {
    const abort = () => reject(Error('aborted'));
    if (signal.aborted) return abort();
    signal.addEventListener('abort', abort, {once: true});
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
  customElements.define('fh-product-recommendations', class extends HTMLElement {
    connectedCallback() {
      if (this.dispose) return;
      const content = this.querySelector('[data-recommendation-content]');
      const feedback = this.querySelector('[data-recommendation-feedback]');
      const status = this.querySelector('[data-recommendation-status]');
      const retry = this.querySelector('[data-recommendation-retry]');
      if (!content || !feedback || !status || !retry) return;
      let controller, observer, resizeObserver, timer, revision = 0;
      const current = token => this.isConnected && token === revision;
      const load = async () => {
        if (this.dataset.state === 'loading' || this.dataset.state === 'ready') return;
        const token = ++revision;
        controller?.abort();
        const requestController = new AbortController();
        controller = requestController;
        const signal = requestController.signal;
        const started = performance.now();
        const checkDeadline = () => {if (signal.aborted || performance.now() - started >= DEADLINE_MS) throw Error('deadline');};
        timer = setTimeout(() => requestController.abort(), DEADLINE_MS);
        this.dataset.state = 'loading';
        this.setAttribute('aria-busy', 'true');
        feedback.hidden = false;
        status.textContent = 'Finding more favorites…';
        retry.hidden = document.activeElement !== retry;
        retry.setAttribute('aria-disabled', 'true');
        // Retain this width's shell and feedback reservation after hydration.
        this.style.minHeight = `${Math.ceil(this.getBoundingClientRect().height)}px`;
        let reader;
        try {
          const url = new URL(this.dataset.url, location.href);
          if (url.origin !== location.origin || url.username || url.password || url.hash || !/\/recommendations\/products$/.test(url.pathname) || url.searchParams.get('section_id') !== this.dataset.sectionId || url.searchParams.get('product_id') !== this.dataset.productId || url.searchParams.get('limit') !== '10' || url.searchParams.get('intent') !== 'related') throw Error('invalid_request');
          const response = await withAbort(fetch(url, {signal, credentials: 'same-origin', redirect: 'error', headers: {Accept: 'text/html'}}), signal);
          checkDeadline();
          if (!response.ok || !response.headers.get('content-type')?.includes('text/html') || !response.body) throw Error('invalid_response');
          if (Number(response.headers.get('content-length')) > MAX_BYTES) throw Error('response_limit');
          reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8', {fatal: true});
          let bytes = 0, reads = 0, html = '';
          while (true) {
            const chunk = await withAbort(reader.read(), signal);
            checkDeadline();
            if (chunk.done) break;
            bytes += chunk.value.byteLength;
            if (bytes > MAX_BYTES) throw Error('response_limit');
            html += decoder.decode(chunk.value, {stream: true});
            if (++reads % 128 === 0) {
              await withAbort(new Promise(resolve => setTimeout(resolve, 0)), signal);
              checkDeadline();
            }
          }
          html += decoder.decode();
          checkDeadline();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const section = Array.from(parsed.querySelectorAll('fh-product-recommendations')).find(node => node.dataset.sectionId === this.dataset.sectionId && node.dataset.productId === this.dataset.productId);
          const fragment = section?.querySelector('[data-recommendation-content]');
          const result = fragment?.querySelector('[data-recommendation-result]');
          if (!fragment || !['ready', 'empty'].includes(result?.dataset.recommendationResult)) throw Error('missing_section');
          checkDeadline();
          if (!current(token) || signal.aborted) return;
          fragment.querySelectorAll('script').forEach(node => node.remove());
          // Replacement removes a keyboard-focused fallback link. Capture focus
          // immediately before mutation, not at fetch start: the customer may
          // have moved elsewhere while the response was pending.
          const focused = document.activeElement;
          const focusWasInContent = content.contains(focused);
          const focusedHref = focusWasInContent ? focused.closest('a')?.getAttribute('href') : null;
          content.replaceChildren(...fragment.childNodes);
          if (result.dataset.recommendationResult === 'empty') this.style.minHeight = '';
          this.dataset.state = 'ready';
          this.setAttribute('aria-busy', 'false');
          if (focusWasInContent || focused === retry) {
            const matchingLink = focusedHref ? Array.from(content.querySelectorAll('a')).find(link => link.getAttribute('href') === focusedHref) : null;
            (matchingLink || content.querySelector('a'))?.focus({preventScroll: true});
          }
          feedback.hidden = true;
        } catch {
          if (!current(token)) return;
          this.dataset.state = 'error';
          this.style.minHeight = '';
          this.setAttribute('aria-busy', 'false');
          status.textContent = 'Recommendations couldn’t load. Try again or explore all products.';
          retry.hidden = false;
          retry.removeAttribute('aria-disabled');
        } finally {
          if (current(token)) clearTimeout(timer);
          requestController.abort();
          reader?.cancel().catch(() => {});
        }
      };
      retry.addEventListener('click', load);
      if ('ResizeObserver' in window) {
        let previousWidth = this.getBoundingClientRect().width;
        resizeObserver = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect.width;
          if (width !== undefined && Math.abs(width - previousWidth) > 1) {
            previousWidth = width;
            this.style.minHeight = '';
          }
        });
        resizeObserver.observe(this);
      }
      this.dispose = () => {
        revision++;
        controller?.abort();
        clearTimeout(timer);
        observer?.disconnect();
        resizeObserver?.disconnect();
        this.style.minHeight = '';
        retry.removeEventListener('click', load);
        if (this.dataset.state === 'loading') this.dataset.state = 'idle';
      };
      if (this.dataset.state !== 'ready') {
        if ('IntersectionObserver' in window) {
          observer = new IntersectionObserver(entries => {if (entries.some(entry => entry.isIntersecting)) {observer.disconnect(); load();}}, {rootMargin: '800px 0px'});
          observer.observe(this);
        } else load();
      }
    }
    disconnectedCallback() {this.dispose?.(); this.dispose = null;}
  });
})();
